import React, { useEffect, useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS as DndKitCSS } from '@dnd-kit/utilities';
import { Dots, RecycleBin } from '@sharefiledev/icons';
import { Avatar, Button, Col, Flex, Form, Input, Row } from 'antd';
import { getSignerColor } from '../../../common/componentUtils';
import { useGetScreenSize } from '../../../common/hooks/useGetScreenSize';
import { ICON_SIZE, MAX_NAME_LENGTH } from '../../../constants';
import { Role } from '../../../data/interfaces/DocumentTemplate';
import { InputValidation } from '../../../data/interfaces/form';
import { t } from '../../../utils';

type RoleItemProps = {
	id: number;
	role: Role;
	onRemove: (id: string) => void;
	onUpdate: (obj: Partial<Role>, id: string) => void;
	signerSequencing: boolean;
	onSequenceInputChange: (currentIndex: number, newIndex: number) => void;
	rolesLength: number;
	isShownInModal: boolean;
	validationState: InputValidation;
};

function RoleItem({
	id,
	role,
	onRemove,
	onUpdate,
	signerSequencing,
	onSequenceInputChange,
	rolesLength,
	isShownInModal,
	validationState,
}: RoleItemProps) {
	const { isExtraSmallScreen } = useGetScreenSize();
	const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
		id: role.id,
	});

	const [sequence, setSequence] = useState(role.sequence);
	const [oldSequence, setOldSequence] = useState(role.sequence);
	const [sequenceDisplayValue, setSequenceDisplayValue] = useState(sequence + 1);

	const transformWithoutScale = transform
		? {
				x: transform.x,
				y: transform.y,
				scaleX: 1,
				scaleY: 1,
		  }
		: undefined;

	const style = {
		transform: DndKitCSS.Transform.toString(transformWithoutScale),
		transition,
	};

	function handleRoleNameChange(updatedName) {
		onUpdate(updatedName, role.id);
	}

	function handleSequenceInputChange(event) {
		const value = event.target.value;
		setOldSequence(sequence);
		if (value === '') {
			setSequence(value);
			setSequenceDisplayValue(value);
		} else {
			const newIndex = Number(value) - 1;

			if (newIndex >= 0 && newIndex < rolesLength) {
				setSequence(newIndex);
				onSequenceInputChange(Number(oldSequence), newIndex);
			}
		}
	}
	const handleBlur = () => {
		if (sequence === undefined) {
			setSequence(oldSequence);
			setSequenceDisplayValue(Number(role.sequence) + 1);
		}
	};

	useEffect(() => {
		setSequence(role.sequence);
		setSequenceDisplayValue(Number(role.sequence) + 1);
	}, [role.sequence]);

	return (
		<Flex vertical ref={setNodeRef} style={style}>
			<Row
				gutter={isShownInModal ? 0 : { xs: 8, sm: 16, md: 24, lg: 32 }}
				justify="space-around"
				align="top"
			>
				<Col span={22}>
					<Row gutter={isShownInModal ? 0 : 8}>
						{signerSequencing && rolesLength > 1 && (
							<Col style={{ maxWidth: 'min-content' }}>
								<Flex
									vertical
									gap={6}
									aria-label={t('startPage.dragRoleAriaLabel', {
										roleName: role.name ? role.name : id,
									})}
								>
									<Dots {...attributes} {...listeners} size={ICON_SIZE} />
									<Input
										type="text"
										onChange={e => handleSequenceInputChange(e)}
										onBlur={handleBlur}
										value={sequenceDisplayValue}
										maxLength={MAX_NAME_LENGTH}
										style={{
											width: 20,
											paddingRight: 0,
											paddingLeft: 0,
											textAlign: 'center',
										}}
										aria-label={t('startPage.sequenceInputAriaLabel', {
											roleName: role.name ? role.name : id,
										})}
									/>
								</Flex>
							</Col>
						)}
						<Col xs={20} sm={20} md={20} lg={20}>
							<Form.Item
								label={t('startPage.roleName')}
								required
								validateStatus={validationState.status}
								help={validationState.message}
							>
								<Input
									prefix={
										<Avatar
											size={16}
											style={{
												background: getSignerColor(role.sequence),
											}}
										/>
									}
									onChange={e => handleRoleNameChange(e.target.value)}
									value={role.name}
									style={{
										width: isExtraSmallScreen ? 150 : 300,
									}}
									aria-label={t('startPage.roleName')}
								/>
							</Form.Item>
						</Col>
					</Row>
				</Col>
				<Col span={2}>
					<Button
						type="text"
						icon={<RecycleBin size={ICON_SIZE} />}
						onClick={() => onRemove(role.id)}
						aria-label={t('startPage.deleteButtonAriaLabel', {
							roleName: role.name ? role.name : id,
						})}
					/>
				</Col>
			</Row>
		</Flex>
	);
}

export default RoleItem;
